import { Container, Graphics } from 'pixi.js';
import { palette } from './theme';

/**
 * Factories that create reusable Pixi.Graphics sprites for each entity kind.
 * Shapes are drawn once here; reconciler repositions/tints them per frame.
 */

export function makePlayerSprite(): Graphics {
  const g = new Graphics();
  // Nose-up triangle with engine glow detail
  g.moveTo(0, -14)
    .lineTo(10, 10)
    .lineTo(0, 5)
    .lineTo(-10, 10)
    .lineTo(0, -14)
    .fill(palette.playerFill)
    .stroke({ color: palette.playerOutline, width: 1.5 });
  // Cockpit highlight
  g.circle(0, -5, 3).fill(0xffffff);
  return g;
}

export function makeEnemyGruntSprite(): Graphics {
  const g = new Graphics();
  // Fighter body — pointed nose down
  g.moveTo(0, 12)
    .lineTo(5, 0)
    .lineTo(4, -8)
    .lineTo(0, -12)
    .lineTo(-4, -8)
    .lineTo(-5, 0)
    .closePath()
    .fill(palette.enemyGrunt);
  // Swept wings
  g.moveTo(5, 2)
    .lineTo(14, 9)
    .lineTo(5, 8)
    .closePath()
    .fill(0xcc3333);
  g.moveTo(-5, 2)
    .lineTo(-14, 9)
    .lineTo(-5, 8)
    .closePath()
    .fill(0xcc3333);
  // Cockpit tint
  g.circle(0, -4, 3).fill(0xff9999);
  return g;
}

export function makeEnemyWeaverSprite(): Graphics {
  const g = new Graphics();
  // Saucer rim
  g.moveTo(-12, 2)
    .lineTo(-9, -2)
    .lineTo(9, -2)
    .lineTo(12, 2)
    .lineTo(9, 8)
    .lineTo(-9, 8)
    .closePath()
    .fill(0xcc8800);
  // Dome
  g.moveTo(-7, 2)
    .lineTo(-5, -6)
    .lineTo(5, -6)
    .lineTo(7, 2)
    .closePath()
    .fill(palette.enemyWeaver);
  // Eye
  g.circle(0, 4, 4).fill(0x111111);
  g.circle(-1, 3, 1.5).fill(0xff8800);
  return g;
}

export function makeEnemySniperSprite(): Graphics {
  const g = new Graphics();
  // Narrow elongated hull
  g.moveTo(0, -18)
    .lineTo(4, -8)
    .lineTo(6, 8)
    .lineTo(3, 14)
    .lineTo(-3, 14)
    .lineTo(-6, 8)
    .lineTo(-4, -8)
    .closePath()
    .fill(palette.enemySniper);
  // Side fins
  g.moveTo(6, 2)
    .lineTo(12, 6)
    .lineTo(6, 10)
    .closePath()
    .fill(0x8844cc);
  g.moveTo(-6, 2)
    .lineTo(-12, 6)
    .lineTo(-6, 10)
    .closePath()
    .fill(0x8844cc);
  // Barrel
  g.rect(-2, -24, 4, 10).fill(0xcc99ff);
  // Lens glow
  g.circle(0, -20, 2.5).fill(0xffffff);
  return g;
}

export function makeEnemySwarmSprite(): Graphics {
  const g = new Graphics();
  // Bug body — oval abdomen
  g.moveTo(0, -8)
    .lineTo(4, -4)
    .lineTo(5, 4)
    .lineTo(0, 8)
    .lineTo(-5, 4)
    .lineTo(-4, -4)
    .closePath()
    .fill(palette.enemySwarm);
  // Head
  g.circle(0, -9, 3.5).fill(0xffcc00);
  // Eyes
  g.circle(-1.5, -9.5, 1).fill(0x000000);
  g.circle(1.5, -9.5, 1).fill(0x000000);
  // Wings (semi-transparent via alpha trick — just use lighter color)
  g.moveTo(-5, 0)
    .lineTo(-11, -6)
    .lineTo(-9, 4)
    .closePath()
    .fill(0xffffaa);
  g.moveTo(5, 0)
    .lineTo(11, -6)
    .lineTo(9, 4)
    .closePath()
    .fill(0xffffaa);
  return g;
}

export function makeEnemySnakeSprite(): Graphics {
  const g = new Graphics();
  // Elongated teardrop body — nose down
  g.moveTo(0, 10)
    .lineTo(6, 0)
    .lineTo(5, -8)
    .lineTo(0, -12)
    .lineTo(-5, -8)
    .lineTo(-6, 0)
    .closePath()
    .fill(palette.enemySnake);
  // Scale-like ridges
  g.moveTo(-4, -4).lineTo(4, -4).stroke({ color: 0x22cc88, width: 1 });
  g.moveTo(-3, 0).lineTo(3, 0).stroke({ color: 0x22cc88, width: 1 });
  // Eye
  g.circle(0, -6, 2.5).fill(0x001a0a);
  g.circle(-0.5, -6.5, 1).fill(0x88ffcc);
  return g;
}

export function makeEnemyOrbitSprite(): Graphics {
  const g = new Graphics();
  // Compact arrowhead — designed to look menacing in circular formation.
  // Body: rounded diamond
  g.moveTo(0, -13)
    .lineTo(8, -2)
    .lineTo(6, 10)
    .lineTo(0, 14)
    .lineTo(-6, 10)
    .lineTo(-8, -2)
    .closePath()
    .fill(palette.enemyOrbit);
  // Engine glow at back
  g.circle(0, 12, 4).fill(0xff4400);
  g.circle(0, 12, 2).fill(0xffff88);
  // Cockpit
  g.circle(0, -4, 3.5).fill(0x331100);
  g.circle(0, -4, 1.5).fill(0xff8800);
  return g;
}

/**
 * Generic enemy factory based on archetype string. Unknown archetypes fall back to grunt.
 */
export function makeEnemySprite(archetype: string): Graphics {
  switch (archetype) {
    case 'weaver':
    case 'sine':
      return makeEnemyWeaverSprite();
    case 'sniper':
    case 'aimed':
      return makeEnemySniperSprite();
    case 'swarmer':
    case 'swarm':
      return makeEnemySwarmSprite();
    case 'snake':
      return makeEnemySnakeSprite();
    case 'orbit':
      return makeEnemyOrbitSprite();
    case 'grunt':
    case 'straight':
    default:
      return makeEnemyGruntSprite();
  }
}

export function makeBulletSpreadSprite(): Graphics {
  const g = new Graphics();
  g.circle(0, 0, 3).fill(palette.bulletSpread);
  return g;
}

export function makeBulletLaserSprite(): Graphics {
  const g = new Graphics();
  // Long bright bolt
  g.rect(-2, -28, 4, 56).fill(palette.bulletLaser);
  // Bright core
  g.rect(-1, -28, 2, 56).fill(0xffffff);
  return g;
}

export function makeBulletHomingSprite(): Graphics {
  const g = new Graphics();
  g.circle(0, 0, 4).fill(palette.bulletHoming);
  g.circle(0, 0, 1.5).fill(0xffffff);
  return g;
}

export function makeBulletEnemySprite(): Graphics {
  const g = new Graphics();
  g.circle(0, 0, 3).fill(palette.enemyBullet);
  return g;
}

export function makeBulletBossSprite(): Graphics {
  const g = new Graphics();
  g.circle(0, 0, 4).fill(palette.bossBullet);
  return g;
}

export function makePickupSprite(): Graphics {
  const g = new Graphics();
  // Hexagon (base color, reconciler tints based on weapon)
  const r = 8;
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < 6; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 3;
    pts.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  g.moveTo(pts[0]![0], pts[0]![1]);
  for (let i = 1; i < pts.length; i++) g.lineTo(pts[i]![0], pts[i]![1]);
  g.lineTo(pts[0]![0], pts[0]![1]).fill(0xffffff);
  return g;
}

export function makeShieldSprite(): Graphics {
  const g = new Graphics();
  // Hexagonal shield outline around player
  g.circle(0, 0, 20).stroke({ color: palette.shield, width: 2.5 });
  g.circle(0, 0, 22).stroke({ color: palette.shield, width: 1, alpha: 0.4 });
  return g;
}

export function makeParticleSprite(): Graphics {
  const g = new Graphics();
  g.circle(0, 0, 2).fill(0xffffff);
  return g;
}

export function makeBossSprite(): Container {
  const c = new Container();
  const body = new Graphics();
  body.roundRect(-50, -28, 100, 56, 10).fill(palette.boss);
  c.addChild(body);

  // Armor plating detail
  const plate = new Graphics();
  plate.rect(-35, -18, 70, 36).fill(0xdd2222);
  c.addChild(plate);

  const eyeL = new Graphics();
  eyeL.circle(0, 0, 8).fill(0xffff88);
  eyeL.circle(0, 0, 4).fill(0xff4400);
  eyeL.x = -22;
  eyeL.y = -4;
  c.addChild(eyeL);

  const eyeR = new Graphics();
  eyeR.circle(0, 0, 8).fill(0xffff88);
  eyeR.circle(0, 0, 4).fill(0xff4400);
  eyeR.x = 22;
  eyeR.y = -4;
  c.addChild(eyeR);

  // Side cannons
  const canL = new Graphics();
  canL.rect(-4, -4, 8, 20).fill(0xcc1111);
  canL.x = -52;
  canL.y = 0;
  c.addChild(canL);

  const canR = new Graphics();
  canR.rect(-4, -4, 8, 20).fill(0xcc1111);
  canR.x = 52;
  canR.y = 0;
  c.addChild(canR);

  return c;
}
